import { describeFunction, formulaUnits } from '../../utils/formula';
import { flattenTokenNodes, parseTemplate } from './tokenTemplate';

/**
 * Where a formula's function arguments are, read from its text (KPR-350): the
 * pure half of the argument pickers and the value pills. Nothing here touches
 * the DOM or Lexical.
 *
 * The formula is a plain string (d03), so "which argument is the caret in" is
 * a question about text: the scan below reads it the way the formula module's
 * tokenizer does — a `{{reference}}` first, then a "…" or '…' literal with \\
 * and \" escapes (an unclosed quote runs to the end), and a call is a name
 * followed by `(` (spaces allowed between, as the parser allows). A comma
 * separates arguments only at the call's own depth: one inside a literal, a
 * reference or a bracketed group is not a separator.
 *
 * What each argument TAKES is the vendored module's (KPR-349):
 * `describeFunction(name, { units }).params[i]` — its `kind` (element, number,
 * text, unit, choice), its `choices` and, for convert's unit, `measureOf`.
 * Nothing about a function is written here, so a function or a unit added to
 * the module is picked with no change in this file.
 */

const REF_AT = /^\{\{[^{}]*\}\}/;
const IDENT_CHAR = /[A-Za-z0-9_]/;
const IDENT = /^[A-Za-z_][A-Za-z0-9_]*$/;
const WHOLE_REF = /^\{\{[^{}]*\}\}$/;

/**
 * One pass over `text`. Returns
 *   calls     every call, in the order it opens:
 *             { fn, open, close, slots: [{ start, end }] } — `close` is the
 *             index of its `)`, or -1 while it is unclosed (its last slot
 *             then runs to the end of the text);
 *   open      the calls still open at the end, innermost last;
 *   literals  every text literal: { start, end, closed };
 *   inLiteral true when the text ends inside an unclosed literal.
 */
function scan(text) {
	const calls = [];
	const stack = []; // a call, or null for a bracketed group
	const literals = [];
	let inLiteral = false;
	let i = 0;
	while (i < text.length) {
		if (text.startsWith('{{', i)) {
			const ref = REF_AT.exec(text.slice(i));
			if (ref) { i += ref[0].length; continue; }
		}
		const ch = text[i];
		if (ch === '"' || ch === '\'') {
			let j = i + 1;
			let closed = false;
			while (j < text.length) {
				if (text[j] === '\\' && j + 1 < text.length && (text[j + 1] === '\\' || text[j + 1] === ch)) { j += 2; continue; }
				if (text[j] === ch) { j += 1; closed = true; break; }
				j += 1;
			}
			literals.push({ start: i, end: Math.min(j, text.length), closed });
			if (!closed) inLiteral = true;
			i = j;
			continue;
		}
		if (ch === '(') {
			let j = i - 1;
			while (j >= 0 && /\s/.test(text[j])) j -= 1;
			let k = j;
			while (k >= 0 && IDENT_CHAR.test(text[k])) k -= 1;
			const name = text.slice(k + 1, j + 1);
			// `a.round(` and `2round(` are not calls the parser would read.
			if (IDENT.test(name) && !(k >= 0 && /[.0-9]/.test(text[k]) && !/\s/.test(text[k]))) {
				const call = { fn: name, open: i, close: -1, slots: [{ start: i + 1, end: -1 }] };
				calls.push(call);
				stack.push(call);
			} else {
				stack.push(null);
			}
			i += 1;
			continue;
		}
		if (ch === ',') {
			const top = stack[stack.length - 1];
			if (top) {
				top.slots[top.slots.length - 1].end = i;
				top.slots.push({ start: i + 1, end: -1 });
			}
			i += 1;
			continue;
		}
		if (ch === ')') {
			const top = stack.pop();
			if (top) {
				top.slots[top.slots.length - 1].end = i;
				top.close = i;
			}
			i += 1;
			continue;
		}
		i += 1;
	}
	calls.forEach(call => {
		const last = call.slots[call.slots.length - 1];
		if (last.end < 0) last.end = text.length;
	});
	return { calls, open: stack.filter(Boolean), literals, inLiteral };
}

/**
 * What argument `index` of `fn` takes: a param of describeFunction, the last
 * one again for a repeating tail (`sum(a, b, c…)`), or null — an unknown
 * function, or more arguments than it takes. `cache` (a Map) saves the
 * describe call when many literals of one formula are read at once.
 */
export function paramFor(fn, index, cache) {
	if (typeof fn !== 'string' || !Number.isInteger(index) || index < 0) return null;
	const key = fn.toLowerCase();
	let described;
	if (cache && cache.has(key)) described = cache.get(key);
	else {
		described = describeFunction(fn, { units: formulaUnits });
		if (cache) cache.set(key, described);
	}
	if (!described || !Array.isArray(described.params) || !described.params.length) return null;
	const { params } = described;
	if (index < params.length) return params[index];
	const last = params[params.length - 1];
	return last.repeat ? last : null;
}

/**
 * The argument the caret is typing, or null when it is in no call. Read from
 * the text BEFORE the caret (a quote being typed would otherwise swallow the
 * call's own `)` — `convert({{w}}, "k)` is an unclosed literal to the
 * tokenizer) plus the blank after it:
 *
 *   { fn, index,
 *     start      where the argument starts (just after `(` or `,`),
 *     end        the caret, plus any spaces after it,
 *     typed      text[start, caret),
 *     atEnd      nothing but spaces stands between the caret and the
 *                argument's end (a `,`, a `)` or the end of the formula),
 *     next       ',' | ')' | '' — what follows the argument,
 *     inLiteral  the caret is inside a quote that is still open,
 *     slots      the call's earlier arguments, [{ start, end }] }
 */
export function argumentAt(text, caret) {
	if (typeof text !== 'string' || !Number.isInteger(caret) || caret < 0 || caret > text.length) return null;
	const prefix = text.slice(0, caret);
	const read = scan(prefix);
	const call = read.open[read.open.length - 1];
	if (!call) return null;
	const slot = call.slots[call.slots.length - 1];
	let after = caret;
	// Typing inside a pair of quotes already there (`"m|"`): the closing quote
	// is part of what the person is writing, and a pick replaces it too.
	if (read.inLiteral) {
		const open = read.literals[read.literals.length - 1];
		if (open && !open.closed && open.start >= slot.start && text[caret] === prefix[open.start]) after = caret + 1;
	}
	while (after < text.length && /\s/.test(text[after])) after += 1;
	const following = text[after];
	// Inside an open quote, what follows the caret is the quote's own text to
	// the tokenizer; for the person it is what comes after what they typed.
	const atEnd = after >= text.length || following === ',' || following === ')';
	return {
		fn: call.fn,
		index: call.slots.length - 1,
		start: slot.start,
		end: atEnd ? after : caret,
		typed: prefix.slice(slot.start),
		atEnd,
		next: atEnd && after < text.length ? following : '',
		inLiteral: read.inLiteral,
		slots: call.slots.slice(0, -1).map(s => ({ start: s.start, end: s.end })),
	};
}

/** The measure (`mass`, …) of the element an argument holds, when it is one pill whose token says. */
export function measureOfArgument(text, slot, tokens) {
	if (!slot || typeof text !== 'string') return null;
	const held = text.slice(slot.start, slot.end).trim();
	if (!WHOLE_REF.test(held)) return null;
	// `{{ weight }}` is `{{weight}}` to the formula module.
	const inner = token => token.slice(2, -2).trim();
	const node = flattenTokenNodes(tokens).find(entry => typeof entry.token === 'string' && inner(entry.token) === inner(held));
	return node && typeof node.measure === 'string' && node.measure ? node.measure : null;
}

/**
 * Every literal of `text` that is a picked value: closed, the WHOLE of an
 * argument whose kind is `unit` or `choice`, and spelled exactly as one of
 * its choices (`"kg"`, `'d'`). [{ start, end, text, value, label, group }],
 * in order. Anything else — an unknown or retired unit, an alias (`"kilos"`),
 * a value built from parts (`"k" & "g"`), an escape — is not, and stays text:
 * a pill means "a value from the list".
 */
export function choiceLiterals(text) {
	if (typeof text !== 'string' || !text || (!text.includes('"') && !text.includes('\''))) return [];
	const { calls, literals } = scan(text);
	if (!calls.length) return [];
	const cache = new Map();
	const out = [];
	literals.forEach(literal => {
		if (!literal.closed || literal.end - literal.start < 3) return;
		const raw = text.slice(literal.start, literal.end);
		const inner = raw.slice(1, -1);
		if (inner.includes('\\')) return;
		// The innermost call whose argument holds this literal.
		let found = null;
		for (const call of calls) {
			const index = call.slots.findIndex(slot => slot.start <= literal.start && literal.end <= slot.end);
			if (index < 0) continue;
			if (!found || call.open > found.call.open) found = { call, index };
		}
		if (!found) return;
		const slot = found.call.slots[found.index];
		if (text.slice(slot.start, slot.end).trim() !== raw) return;
		const param = paramFor(found.call.fn, found.index, cache);
		if (!param || (param.kind !== 'unit' && param.kind !== 'choice') || !Array.isArray(param.choices)) return;
		const choice = param.choices.find(entry => entry.value === inner);
		if (!choice) return;
		out.push({ start: literal.start, end: literal.end, text: raw, value: choice.value, label: choice.label, group: choice.group });
	});
	return out;
}

/**
 * A stored formula as the editor draws it: `[{ type: 'text', text }]`,
 * `[{ type: 'token', token }]` (an element pill) and
 * `[{ type: 'choice', text, label }]` (a value pill), in order. Joining every
 * segment's text (a token's token) gives back `value` byte for byte.
 */
export function formulaSegments(value) {
	const text = typeof value === 'string' ? value : '';
	const segments = parseTemplate(text, { formula: true });
	const choices = choiceLiterals(text);
	if (!choices.length) return segments;
	const out = [];
	let offset = 0;
	segments.forEach(segment => {
		if (segment.type === 'token') {
			out.push(segment);
			offset += segment.token.length;
			return;
		}
		const from = offset;
		const to = offset + segment.text.length;
		let cursor = from;
		choices.filter(choice => choice.start >= from && choice.end <= to).forEach(choice => {
			if (choice.start > cursor) out.push({ type: 'text', text: text.slice(cursor, choice.start) });
			out.push({ type: 'choice', text: choice.text, label: choice.label, value: choice.value });
			cursor = choice.end;
		});
		if (cursor < to) out.push({ type: 'text', text: text.slice(cursor, to) });
		offset = to;
	});
	return out;
}
